import { Alert, Grid, Stack } from '@mui/material';
import { PropsWithChildren } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { updatePhoneNumber } from '@/api/user';

import {
  getPhoneSlotProps,
  sanitizeCaymanCountryCode,
} from '@/constants/countries';
import { COUNTRY_PHONE_CODES } from '@/constants/countryCodes';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState, selectRightStartFeature } from '@/redux/configs';
import { selectTerritories } from '@/redux/countries';

import { Tenant } from '@/types/common';
import { PhoneForm as PhoneFormType } from '@/types/form';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { getPhoneNumberMaxLengths, getPhoneRules } from '@/utils/phoneNumber';
import { extractLocalPhoneNumber } from '@/utils/user';

import { LoadingButton } from '../Form/LoadingButton';
import { Select } from '../Form/Select';
import { TextInput } from '../Form/TextInput';
import { Icon } from '../Icon/Icon';
import { Form } from '../Layout';
import { DesktopOnly, MobileOnly } from '../Layout/Responsive';

export function PhoneForm({ children }: Readonly<PropsWithChildren>) {
  const { phoneCodes } = useAppSelector(selectTerritories);
  const { extraPhoneOptions, overridePhoneNumberSchema } =
    useAppSelector(selectConfigsState);
  const user = useAppSelector(selectLoggedInUser);
  const { t } = useTranslate();
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const { tenant, phoneNumber } = extractLocalPhoneNumber(
    user?.phoneNumberAlt || '',
  );
  const defaultPhoneNumber = enabledRightStartAccount
    ? (phoneNumber ?? '')
    : '';
  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm<PhoneFormType>({
    defaultValues: {
      countryCode:
        tenant || COUNTRY_PHONE_CODES[user?.tenant.toLowerCase() as Tenant],
      phoneNumber: defaultPhoneNumber,
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updatePhoneNumber,
  });

  const onSubmit = handleSubmit(async payload => {
    const sanitizedPayload = {
      ...payload,
      countryCode: sanitizeCaymanCountryCode(payload.countryCode),
    };
    mutate(sanitizedPayload);
  });

  const countryOptions = phoneCodes.concat(extraPhoneOptions || []);

  return (
    <Form onSubmit={onSubmit}>
      {children}
      <Grid container columnSpacing={1.5} rowSpacing={2}>
        <Grid item xs={12} sm={5}>
          <Select
            options={countryOptions}
            control={control}
            name="countryCode"
            fullWidth
            disabled={isPending}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <Controller
            control={control}
            name="countryCode"
            render={({ field: { value } }) => (
              <TextInput
                type="tel"
                inputMode="tel"
                name="phoneNumber"
                placeholder={t('phone.form.tel.placeHolder')}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
                inputProps={getPhoneSlotProps(
                  value,
                  getPhoneNumberMaxLengths(overridePhoneNumberSchema),
                )}
                rules={getPhoneRules(value, overridePhoneNumberSchema)}
                disabled={isPending}
                control={control}
              />
            )}
          />
        </Grid>
      </Grid>
      {error && (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {getAPIErrorMessage(error)}
        </Alert>
      )}
      <Stack direction="column" alignItems="stretch" gap={2}>
        <MobileOnly>
          <LoadingButton
            {...getDataTestId('phone-action-continue')}
            type="submit"
            disabled={!isValid || isPending}
            isLoading={isPending}
            fullWidth
          >
            {t('phone.form.button.continue')}
          </LoadingButton>
        </MobileOnly>
        <DesktopOnly>
          <LoadingButton
            {...getDataTestId('phone-action-continue')}
            type="submit"
            disabled={!isValid || isPending}
            isLoading={isPending}
            fullWidth
          >
            {t('phone.form.button.continue')}
          </LoadingButton>
        </DesktopOnly>
      </Stack>
    </Form>
  );
}
